import { useABNUpdateDetailsStore } from '@stores/ABNUpdateDetailsStore.js'
import { useBNStore } from '@stores/BNStore.js'
import { useFindStore } from '@stores/FindStore.js'
import { useLookupStore } from '@stores/LookupStore.js'

/**
 * Flatten the `/api/client/profile` payload into the order-like shape the
 * service-form stores consume (`firstname`, flat home address keys,
 * `personal_address_*` / `business_address_*` prefixed keys).
 *
 * @param {object} profile `/api/client/profile` response shape.
 * @returns {object}
 */
function toStorePayload(profile) {
  const home = profile.home_address ?? {}
  const business = profile.business_address ?? {}

  return {
    email: profile.email,
    firstname: profile.first_name,
    middlename: profile.middle_name,
    lastname: profile.last_name,
    phone: profile.phone,
    birthdate: profile.birth_date,
    birth_country: profile.birth_country,
    birth_state: profile.birth_state,
    birth_city: profile.birth_city,

    address: home.full_address,
    unit_number: home.unit_number,
    street_number: home.street_number,
    route: home.route,
    locality: home.locality,
    state: home.state,
    postal_code: home.postal_code,

    personal_address: home.full_address,
    personal_address_unit_number: home.unit_number,
    personal_address_street_number: home.street_number,
    personal_address_route: home.route,
    personal_address_locality: home.locality,
    personal_address_state: home.state,
    personal_address_postal_code: home.postal_code,

    business_address: business.full_address,
    business_address_unit_number: business.unit_number,
    business_address_street_number: business.street_number,
    business_address_route: business.route,
    business_address_locality: business.locality,
    business_address_state: business.state,
    business_address_postal_code: business.postal_code,
  }
}

/**
 * Hydrate the service-form stores from the `/api/client/profile` payload.
 *
 * Shared by SignInBlock (initial hydration on sign-in) and ProfileStore (after a
 * profile/address save), so edited account data is reflected in the service forms
 * when the user navigates the site, without needing a reload.
 *
 * @param {object} profile `/api/client/profile` response shape.
 */
export function hydrateClientStores(profile) {
  const data = toStorePayload(profile)

  useFindStore().update(data)
  useBNStore().update(data)
  useABNUpdateDetailsStore().update({ abn_update_details: data }, { hydrate: true })
  useLookupStore().update(data)
}
